import { useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
} from '@tanstack/react-table';
import {
  useSupplierPriceListItems,
  useSupplierPriceItemHistory,
} from '@/api/supplier-price-lists/supplier-price-lists.hooks';
import type {
  SupplierPriceItem,
  ListItemsFilters,
  SupplierPriceItemSortField,
  PriceHistoryEntry,
} from '@/api/supplier-price-lists/supplier-price-lists.types';
import { DsSearchInput, DsKv } from '@/modules/internal-v2/ds';
import { DsTable } from '../../shared/ds-table.component';
import { ContainedPage } from '../../shared/contained-page.component';

const fmtPrice = (v: string | number, currency: string) =>
  new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: currency || 'EUR',
  }).format(Number(v));

const fmtDateShort = (iso: string | null) => {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
};

const fmtDateTime = (iso: string | null) => {
  if (!iso) return null;
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const Mono = ({ children }: { children: React.ReactNode }) => (
  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>
    {children}
  </span>
);

const Dash = () => <span style={{ color: 'var(--ink-4)' }}>—</span>;

const col = createColumnHelper<SupplierPriceItem>();

const columns = [
  col.accessor('supplierSku', {
    header: 'SKU',
    size: 110,
    cell: (info) => (
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--fs-xs)',
          fontWeight: 500,
        }}
      >
        {info.getValue()}
      </span>
    ),
  }),
  col.accessor('ean', {
    header: 'EAN',
    size: 120,
    cell: (info) => (info.getValue() ? <Mono>{info.getValue()}</Mono> : <Dash />),
  }),
  col.accessor('manufacturerCode', {
    header: 'MPN',
    size: 130,
    cell: (info) => (info.getValue() ? <Mono>{info.getValue()}</Mono> : <Dash />),
  }),
  col.accessor('description', {
    header: 'Bezeichnung',
    meta: { flex: true },
    cell: (info) => {
      const v = info.getValue();
      if (!v) return <Dash />;
      return (
        <span style={{ color: 'var(--ink-2)' }} title={v}>
          {v}
        </span>
      );
    },
  }),
  col.accessor('netPrice', {
    header: 'Preis',
    size: 100,
    meta: { numeric: true },
    cell: (info) => (
      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 500 }}>
        {fmtPrice(info.getValue(), info.row.original.currency)}
      </span>
    ),
  }),
  col.accessor('minQty', {
    header: 'Min.',
    size: 55,
    meta: { numeric: true },
    cell: (info) => (
      <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-2)' }}>
        {info.getValue()}
      </span>
    ),
  }),
  col.accessor('leadTimeDays', {
    header: 'Lief. (T)',
    size: 70,
    meta: { numeric: true },
    cell: (info) => {
      const v = info.getValue();
      return v === null ? (
        <Dash />
      ) : (
        <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-2)' }}>
          {v}
        </span>
      );
    },
  }),
  col.accessor('stockQty', {
    header: 'Lager',
    size: 70,
    meta: { numeric: true },
    cell: (info) => {
      const v = info.getValue();
      if (v === null) return <Dash />;
      const cls = v > 0 ? 'pos' : 'muted';
      return (
        <span
          className={`ds-pill ${cls}`}
          style={{ fontFamily: 'var(--font-mono)', minWidth: 36, justifyContent: 'center' }}
        >
          {v}
        </span>
      );
    },
  }),
  col.display({
    id: 'nextDelivery',
    header: 'Nächste Lieferung',
    size: 150,
    cell: (info) => {
      const e = info.row.original;
      const date = fmtDateShort(e.stockNextDeliveryAt);
      const qty = e.stockNextDeliveryQty;
      if (!date && qty === null) return <Dash />;
      return (
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--fs-xs)',
            color: 'var(--ink-2)',
          }}
        >
          {date ?? '—'}
          {qty !== null && (
            <span style={{ color: 'var(--ink-3)' }}> · {qty}&nbsp;Stk</span>
          )}
        </span>
      );
    },
  }),
  col.accessor('updatedAt', {
    header: 'Aktualisiert',
    size: 115,
    cell: (info) => (
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--fs-xs)',
          color: 'var(--ink-3)',
        }}
      >
        {fmtDateTime(info.getValue())}
      </span>
    ),
  }),
];

const PAGE_SIZE = 100;

interface Props {
  feedId: string;
}

export function SupplierFeedItems({ feedId }: Props) {
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [selectedItem, setSelectedItem] = useState<SupplierPriceItem | null>(
    null,
  );

  const filters: ListItemsFilters = useMemo(
    () => ({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      q: search.trim() || undefined,
      sortField: 'updatedAt' as SupplierPriceItemSortField,
      sortOrder: 'desc' as const,
    }),
    [page, search],
  );

  const { data, isPending } = useSupplierPriceListItems(feedId, filters);
  const items = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });

  return (
    <ContainedPage
      wide
      toolbar={
        <>
          <DsSearchInput
            className="bp-orders-search"
            value={search}
            onSearch={(q) => {
              setSearch(q);
              setPage(0);
            }}
            debounce={300}
            placeholder="SKU, EAN, MPN, Bezeichnung…"
          />
          <div className="bp-orders-pager">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
            >
              ‹
            </button>
            <span>
              <b style={{ color: 'var(--ink)' }}>{total}</b> Artikel · Seite{' '}
              {page + 1}/{totalPages}
            </span>
            <button
              onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
            >
              ›
            </button>
          </div>
        </>
      }
    >
      <DsTable
        table={table}
        fixedLayout
        onRowClick={setSelectedItem}
        selectedRowId={selectedItem?.id}
        emptyMessage={isPending ? 'Laden…' : 'Keine Artikel gefunden.'}
      />

      {selectedItem && (
        <PriceHistoryOverlay
          feedId={feedId}
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
        />
      )}
    </ContainedPage>
  );
}

function PriceHistoryOverlay({
  feedId,
  item,
  onClose,
}: {
  feedId: string;
  item: SupplierPriceItem;
  onClose: () => void;
}) {
  const { data, isPending } = useSupplierPriceItemHistory(feedId, item.id, {
    limit: 100,
  });
  const entries = data?.data ?? [];

  const histCol = createColumnHelper<PriceHistoryEntry>();
  const histColumns = [
    histCol.accessor('changedAt', {
      header: 'Geändert',
      size: 120,
      cell: (info) => (
        <span
          style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}
        >
          {fmtDateTime(info.getValue())}
        </span>
      ),
    }),
    histCol.accessor('changedFields', {
      header: 'Felder',
      size: 140,
      cell: (info) => (
        <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {info.getValue().map((f) => (
            <span key={f} className="ds-pill muted">
              {f}
            </span>
          ))}
        </div>
      ),
    }),
    histCol.display({
      id: 'change',
      header: 'Änderung',
      meta: { flex: true },
      cell: (info) => {
        const e = info.row.original;
        const parts: string[] = [];
        if (e.changedFields.includes('netPrice')) {
          parts.push(
            `${fmtPrice(e.previousNetPrice, e.previousCurrency)} → ${fmtPrice(e.newNetPrice, e.newCurrency)}`,
          );
        }
        if (e.changedFields.includes('minQty')) {
          parts.push(`Menge: ${e.previousMinQty} → ${e.newMinQty}`);
        }
        return (
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-xs)',
              color: 'var(--ink-2)',
            }}
          >
            {parts.join(' · ') || '—'}
          </span>
        );
      },
    }),
  ];

  const histTable = useReactTable({
    data: entries,
    columns: histColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });

  return (
    <div className="bp-overlay">
      <div className="bp-overlay-bar">
        <button className="bp-cmd-back" onClick={onClose}>
          ← Zurück
        </button>
        <span className="bp-overlay-title">Preisverlauf</span>
      </div>
      <div
        style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)' }}
      >
        <DsKv
          keyWidth={80}
          entries={[
            {
              label: 'SKU',
              value: (
                <span style={{ fontFamily: 'var(--font-mono)' }}>
                  {item.supplierSku}
                </span>
              ),
            },
            ...(item.ean
              ? [
                  {
                    label: 'EAN',
                    value: (
                      <span style={{ fontFamily: 'var(--font-mono)' }}>
                        {item.ean}
                      </span>
                    ),
                  },
                ]
              : []),
            {
              label: 'Preis',
              value: (
                <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                  {fmtPrice(item.netPrice, item.currency)}
                </span>
              ),
            },
          ]}
        />
      </div>
      <DsTable
        table={histTable}
        fixedLayout
        emptyMessage={isPending ? 'Laden…' : 'Kein Preisverlauf.'}
      />
    </div>
  );
}
